import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { format, isValid, parseISO } from 'date-fns'

import { entitySearchService } from '@/modules/shared/services/entity-search.service'
import type { Entity } from '@/modules/shared/types/entity.type'
import type { PayloadSearch } from '@/modules/shared/types/payload-search.type'
import { usePromotionVisit, type PromotionVisit } from '../hooks/use-promotion-visit.hook'
import { usePromotionSearch } from './use-promotion-search.hook'

type ModalType = 'promoter' | 'client' | null

// Tipo para armazenar os parâmetros de pesquisa
type SearchParams = {
	entityType: string
	filter: string
	inputValue: string
	dataInclusao?: string
}

export function usePromotionVisitForm(props: {
	getVisit: (id: string) => Promise<PromotionVisit>
	saveVisit: (visit: PromotionVisit) => Promise<PromotionVisit>
	isLoading: boolean
}) {
	const { getVisit, saveVisit, isLoading: isLoadingVisit } = props
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const location = useLocation()
	const { handleSelectPromotionToStartActions } = usePromotionSearch()

	// Form state
	const [promotorId, setPromotorId] = useState<number | null>(null)
	const [promotorData, setPromotorData] = useState<Entity | null>(null)
	const [valor, setValor] = useState<number>(0)
	const [observacao, setObservacao] = useState('')
	const [visitDate, setVisitDate] = useState<Date | undefined>(new Date())
	const [selectedClients, setSelectedClients] = useState<Entity[]>([])
	const [dataInclusao, setDataInclusao] = useState<string | null>(null)
	const [visit, setVisit] = useState<PromotionVisit | null>(null)

	// Entity search state
	const [openModal, setOpenModal] = useState<ModalType>(null)
	const [promoters, setPromoters] = useState<Entity[]>([])
	const [clients, setClients] = useState<Entity[]>([])
	const [isLoadingEntities, setIsLoadingEntities] = useState(false)
	const [isLoadingMore, setIsLoadingMore] = useState(false)
	const [currentPage, setCurrentPage] = useState(1)
	const [totalRecordsPromoters, setTotalRecordsPromoters] = useState(0)
	const [totalRecordsCustomers, setTotalRecordsCustomers] = useState(0)
	const [hasMorePromoters, setHasMorePromoters] = useState(false)
	const [hasMoreCustomers, setHasMoreCustomers] = useState(false)

	// Novos estados para persistir os parâmetros de pesquisa
	const [lastPromoterSearchParams, setLastPromoterSearchParams] = useState<SearchParams | null>(null)
	const [lastClientSearchParams, setLastClientSearchParams] = useState<SearchParams | null>(null)

	// Form status
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [dataLoaded, setDataLoaded] = useState(false)
	const [loadError, setLoadError] = useState<string | null>(null)

	const isEditMode = !!id
	const isViewOnly = location.pathname.includes('/visualizar')

	// Função para atualizar a URL com parâmetros de busca
	const updateUrlParams = (params: Record<string, string>) => {
		const searchParams = new URLSearchParams(location.search)

		Object.entries(params).forEach(([key, value]) => {
			if (value) {
				searchParams.set(key, value)
			} else {
				searchParams.delete(key)
			}
		})

		const newUrl = `${location.pathname}?${searchParams.toString()}`
		navigate(newUrl, { replace: true })
	}

	// Função para ler parâmetros da URL
	const getUrlParams = () => {
		const searchParams = new URLSearchParams(location.search)
		return {
			promoterFilter: searchParams.get('promoterFilter'),
			promoterInput: searchParams.get('promoterInput'),
			clientFilter: searchParams.get('clientFilter'),
			clientInput: searchParams.get('clientInput')
		}
	}

	const parseDateSafely = (dateString: string | null | undefined): Date | undefined => {
		if (!dateString) return undefined;

		try {

			console.log("Parsing date:", dateString);
			const date = dayjs(dateString).add(3, 'hours').toDate(); // Adiciona 3 horas para corrigir o fuso horário
			return isValid(date) ? date : undefined;
		} catch (error) {
			console.error("Failed to parse date:", dateString, error);
			return undefined;
		}
	};

	useEffect(() => {
		if (isEditMode && id && !dataLoaded) {
			const fetchVisitData = async () => {
				try {
					const visitData = await getVisit(id);

					if (visitData) {
						setVisit(visitData)
						if (visitData.promotor) {
							if (typeof visitData.promotor === 'object') {
								const promoterObj = visitData.promotor as Entity;
								setPromotorId(promoterObj.idEntidade || visitData.fkPromotor);
								setPromotorData({
									idEntidade: promoterObj.idEntidade || visitData.fkPromotor,
									razaoSocialNome: promoterObj.razaoSocialNome || '',
									cnpjCpf: promoterObj.cnpjCpf || '',
									logradouro: promoterObj.logradouro || '',
									numero: promoterObj.numero || '',
									cidade: promoterObj.cidade || '',
									uf: promoterObj.uf || ''
								} as Entity);
							} else {
								setPromotorId(visitData.fkPromotor);
								setPromotorData({
									idEntidade: visitData.fkPromotor,
									razaoSocialNome: String(visitData.promotor),
									cnpjCpf: '',
									logradouro: '',
									numero: '',
									cidade: '',
									uf: ''
								} as Entity);
							}
						}

						setValor(visitData.valor || 0);
						setObservacao(visitData.observacao || '');

						if (visitData.data) {
							setVisitDate(parseDateSafely(visitData.data));
						}

						if (visitData.dataInclusao) {
							setDataInclusao(visitData.dataInclusao);
						} else if (visitData.dataInclusaoServidor) {
							setDataInclusao(visitData.dataInclusaoServidor);
						}

						const initialClients = visitData.promotorCliente?.length
							? visitData.promotorCliente
							: [];

						// Filtrar clientes válidos e remover undefineds
						const validClients = initialClients
							.map(item => item?.cliente)
							.filter((cliente): cliente is Entity => cliente != null && cliente.idEntidade != null);

						setSelectedClients(validClients);
						setDataLoaded(true);
					}
				} catch (error) {
					console.error('Error loading visit data:', error);
					setLoadError(error instanceof Error ? error.message : 'Erro ao carregar dados da visita');
					toast.error('Erro ao carregar dados da visita');
					setDataLoaded(true);
				}
			};

			fetchVisitData();
		}
	}, [id, isEditMode, getVisit, dataLoaded]);

	// Função para buscar entidades usando o serviço
	const searchEntities = async (entityType: string, filter: string, inputValue: string) => {
		try {
			setIsLoadingEntities(true);
			setCurrentPage(1);

			const payload = {
				entityType,
				filter,
				inputValue: inputValue || ' ',
				dataInclusao: entityType === '1' ? dataInclusao || undefined : undefined
			};

			// Salvar os parâmetros de pesquisa para uso na paginação
			const searchParams: SearchParams = {
				entityType,
				filter,
				inputValue: inputValue || ' ',
				dataInclusao: entityType === '1' ? dataInclusao || undefined : undefined
			}

			if (entityType === '5') {
				setLastPromoterSearchParams(searchParams)
				// Atualizar URL com parâmetros do promoter
				updateUrlParams({
					promoterFilter: filter,
					promoterInput: inputValue || ' '
				})
			} else if (entityType === '1') {
				setLastClientSearchParams(searchParams)
				// Atualizar URL com parâmetros do cliente
				updateUrlParams({
					clientFilter: filter,
					clientInput: inputValue || ' '
				})
			}

			const { entities, hasMore, totalRecords } = await entitySearchService.searchInitialEntities(payload);

			if (entityType == '5') {
				setPromoters(entities);
				setHasMorePromoters(hasMore);
				setTotalRecordsPromoters(totalRecords);
			} else if (entityType == '1') {
				setClients(entities);
				setHasMoreCustomers(hasMore);
				setTotalRecordsCustomers(totalRecords);
			}
		} catch (error) {
			console.error(`Error searching ${entityType === '5' ? 'promoters' : 'clients'}:`, error);
			toast.error(`Erro ao buscar ${entityType === '5' ? 'promotores' : 'clientes'}`);
		} finally {
			setIsLoadingEntities(false);
		}
	};

	const loadMorePromoters = async () => {
		if (!hasMorePromoters || isLoadingMore || !lastPromoterSearchParams) return;

		try {
			setIsLoadingMore(true);
			const nextPage = currentPage + 1;

			// Usar os parâmetros de pesquisa salvos ao invés de valores hardcoded
			const response = await entitySearchService.loadMoreEntities(
				lastPromoterSearchParams,
				nextPage,
				promoters.length,
				totalRecordsPromoters
			);

			setPromoters(prev => [...prev, ...response.entities]);
			setCurrentPage(nextPage);
			setHasMorePromoters(response.hasMore);
		} catch (error) {
			console.error('Error loading more promoters:', error);
			toast.error('Erro ao carregar mais promotores');
		} finally {
			setIsLoadingMore(false);
		}
	};

	const loadMoreCustomers = async () => {
		if (!hasMoreCustomers || isLoadingMore || !lastClientSearchParams) return;

		try {
			setIsLoadingMore(true);
			const nextPage = currentPage + 1;

			// Usar os parâmetros de pesquisa salvos ao invés de valores hardcoded
			const response = await entitySearchService.loadMoreEntities(
				lastClientSearchParams,
				nextPage,
				clients.length,
				totalRecordsCustomers
			);

			setClients(prev => [...prev, ...response.entities]);
			setCurrentPage(nextPage);
			setHasMoreCustomers(response.hasMore);
		} catch (error) {
			console.error('Error loading more customers:', error);
			toast.error('Erro ao carregar mais clientes');
		} finally {
			setIsLoadingMore(false);
		}
	};

	const handleSearchPromoter = ({ payload }: PayloadSearch) => {
		searchEntities('5', payload.filter, payload.inputValue || '%20');
	}

	const handleSearchClient = ({ payload }: PayloadSearch) => {
		searchEntities('1', payload.filter, payload.inputValue || '%20');
	}

	// Restaurar filtros da URL quando o modal é aberto
	useEffect(() => {
		if (openModal) {
			const urlParams = getUrlParams()

			// Se há parâmetros na URL e não há pesquisa salva, fazer busca inicial
			if (openModal === 'promoter' && urlParams.promoterFilter && urlParams.promoterInput && !lastPromoterSearchParams) {
				searchEntities('5', urlParams.promoterFilter, urlParams.promoterInput)
			} else if (openModal === 'client' && urlParams.clientFilter && urlParams.clientInput && !lastClientSearchParams) {
				searchEntities('1', urlParams.clientFilter, urlParams.clientInput)
			}
		}
	}, [openModal])

	useEffect(() => {
		setDataLoaded(false)
		setLoadError(null)
	}, [id])

	const handleSelectPromoter = (promoter: Entity) => {
		if (isViewOnly) return

		setPromotorId(promoter.idEntidade)
		setPromotorData(promoter)
		setOpenModal(null)
		// Limpar os resultados de busca
		setPromoters([])
	}

	const handleSelectClient = (client: Entity) => {
		if (isViewOnly) return

		// Verificar se o cliente é válido antes de adicionar
		if (!client || !client.idEntidade) {
			console.warn('Tentativa de selecionar cliente inválido:', client)
			return
		}

		// Verificar se já não está selecionado
		if (!selectedClients.some(c => c?.idEntidade === client.idEntidade)) {
			setSelectedClients(prev => [...prev, client])
		}
	}

	const handleRemoveClient = (clientId: number) => {
		if (isViewOnly) return
		setSelectedClients(prev => prev.filter(c => c?.idEntidade !== clientId))
	}

	const handleRemovePromoter = () => {
		if (isViewOnly) return
		setPromotorId(null)
		setPromotorData(null)
	}

	const handleCloseModal = () => {
		setOpenModal(null)
		setPromoters([])
		setClients([])
		setCurrentPage(1)
		setTotalRecordsPromoters(0)
		setTotalRecordsCustomers(0)
		setHasMorePromoters(false)
		setHasMoreCustomers(false)
		// Limpar parâmetros de pesquisa da URL quando fechar o modal
		updateUrlParams({
			promoterFilter: '',
			promoterInput: '',
			clientFilter: '',
			clientInput: ''
		})
		// Limpar parâmetros salvos
		setLastPromoterSearchParams(null)
		setLastClientSearchParams(null)
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		handleSelectPromotionToStartActions(undefined)

		if (isViewOnly) return

		if (!promotorId || selectedClients.length === 0 || !visitDate) {
			toast.error('Preencha todos os campos obrigatórios')
			return
		}

		setIsSubmitting(true)

		try {
			// Filtrar clientes válidos
			const validClients = selectedClients.filter((client): client is Entity => {
				if (!client) {
					console.warn('Cliente undefined encontrado')
					return false
				}
				if (!client.idEntidade) {
					console.warn('Cliente sem idEntidade:', client)
					return false
				}
				return true
			})

			if (validClients.length === 0) {
				toast.error('Nenhum cliente válido selecionado')
				return
			}

			const basePayload: PromotionVisit = {
				...visit, // Spread do visit existente (pode ser null)
				promotor: promotorData,
				fkPromotor: promotorId,
				valor,
				observacao,
				// CORREÇÃO: Usar dayjs com startOf('day') para garantir que não haja problemas de timezone
				data: dayjs(visitDate).startOf('day').format('YYYY-MM-DD') + 'T00:00:00',
				promotorCliente: validClients.map(client => ({
					cliente: client,
					fkCliente: client.idEntidade,
					fkUsuarioMobile: visit?.fkUsuarioMobile || null,
					fkVisita: visit?.idVisita || null,
					fkUsuarioxDispositivo: visit?.fkUsuarioxDispositivo || null,
				}))
			}

			const payload = isEditMode ? {
				...basePayload,
				idVisita: Number(id),
			} : basePayload

			await saveVisit(payload)
			toast.success(`Visita ${isEditMode ? 'atualizada' : 'criada'} com sucesso`)
			navigate('/visitas')
		} catch (error) {
			toast.error(`Erro ao ${isEditMode ? 'atualizar' : 'criar'} visita`)
			console.error('Erro no submit:', error)
		} finally {
			setIsSubmitting(false)
		}
	}

	return {
		// Form data
		promotorId,
		promotorData,
		valor,
		setValor,
		observacao,
		setObservacao,
		visitDate,
		setVisitDate,
		selectedClients,
		dataInclusao,

		// Entity search data
		openModal,
		setOpenModal,
		promoters,
		customers: clients,
		isLoadingEntities,
		isLoadingMore,
		hasMorePromoters,
		hasMoreCustomers,
		totalRecordsPromoters,
		totalRecordsCustomers,
		loadMorePromoters,
		loadMoreCustomers,

		// Form state
		isSubmitting,
		dataLoaded,
		loadError,
		isLoadingVisit,

		// Mode flags
		isEditMode,
		isViewOnly,

		// Handlers
		handleSearchPromoter,
		handleSearchClient,
		handleSelectPromoter,
		handleSelectClient,
		handleRemoveClient,
		handleRemovePromoter,
		handleCloseModal,
		handleSubmit,
		navigateBack: () => {
			handleSelectPromotionToStartActions(undefined)
			navigate(-1)
		}
	}
}